import * as Comlink from 'comlink';
import { signIn } from 'next-auth/react';
import getFingerprint from 'lib/fingerprint';
import { HBAuthMessage } from './types';
import { isBookkeepingRoute } from '../isBookkeepingRoute';
import { createContext, useContext, useEffect, useRef } from 'react';
import { empty } from '@apollo/client';
import { logger } from '@finance/shared';
import { setColorModeParam } from 'lib/colorMode';

export type SidenavStates = 'off' | 'collapsed' | 'pinned';

// Subset of `TFeature` in honeybook-react. Add a key here before reading it via `useIsFeatureHidden`.
export type HiddenFeatureKey =
    | 'finance_overview_carousel'
    | 'tax_hub_non_us_sections'
    | 'balance'
    | 'capital'
    | 'quickbooks'
    | 'plaid_bank_connections';

export type HiddenFeaturesMap = Partial<Record<HiddenFeatureKey, boolean>>;

export type TAuthAdapter = {
    triggerLoad: () => void;
    getToken: () => void;
    getSidenavState: () => SidenavStates;
    toggleModal: (isOpen: boolean) => void;
    triggerAppCallback: (name: string) => unknown;
    signout: () => void;
};

type CallbackNames = 'sideNaveStateChange' | 'colorModeChange';

let activeLink: AuthLink | null = null;

const AuthLinkContext = createContext<{ link: AuthLink | null }>({ link: null });

export const AuthLinkProvider: React.FC = props => {
    const authLink = useRef(new AuthLink());
    activeLink = authLink.current;
    useEffect(() => {
        authLink.current.init();
    }, []);

    return <AuthLinkContext.Provider value={{ link: authLink.current }}>{props.children}</AuthLinkContext.Provider>;
};

export function useAuthLink() {
    const payload = useContext(AuthLinkContext);

    if (!payload) {
        throw new Error('missing authlink context');
    }

    return payload.link;
}

let modalCounter = 0;
export function toggleModal(isOpen: boolean) {
    // In order to address modal on modal scenarios, we need to keep track of the number of modal opens and closes.
    modalCounter += isOpen ? 1 : -1;
    if (modalCounter < 0) {
        modalCounter = 0;
    }
    activeLink?.toggleModal(Boolean(modalCounter > 0));
}

export function authLinkSignout() {
    activeLink?.signout();
}

export function clearAuthLink() {
    activeLink = null;
}

export class AuthLink {
    private endpoint?: Comlink.Endpoint;
    private adapter?: Comlink.Remote<TAuthAdapter>;
    private callbacks: Map<CallbackNames, <Payload = unknown>(data: Payload) => void>;
    private initialized = false;
    ready: Promise<boolean>;
    private setReady: () => void = empty;

    constructor() {
        this.callbacks = new Map();
        this.ready = new Promise(r => {
            this.setReady = r as () => void;
        });
    }

    init() {
        if (this.initialized) {
            return;
        }
        this.endpoint = Comlink.windowEndpoint(window.parent, window);
        this.adapter = Comlink.wrap<TAuthAdapter>(this.endpoint);

        Comlink.expose(
            {
                checkIsFinanceReady: async () => this.notifyReady(),
                handleAuth: async (data: HBAuthMessage) => this.handleAuth(data),
                setSideNavState: async (state: SidenavStates) => {
                    this.callbacks.get('sideNaveStateChange')?.(state);
                },
                setColorMode: async (mode: string) => {
                    this.callbacks.get('colorModeChange')?.(mode);
                },
                honeybookReady: () => this.setReady()
            },
            this.endpoint
        );

        this.initialized = true;
        return this.adapter.triggerLoad();
    }

    private async notifyReady() {
        try {
            await this.adapter?.triggerLoad();
        } catch (error) {
            // Silently handle any errors from triggerLoad
            logger.error('Error in triggerLoad:', error);
        }
    }

    private buildCallbackUrl(data: HBAuthMessage) {
        const { callbackUrl, source, irf } = data;
        const colorMode = new URLSearchParams(window.location.search).get('color_mode');

        if (!callbackUrl || (!source && !irf && !colorMode)) {
            return callbackUrl;
        }

        if (!source && !irf) {
            return setColorModeParam(callbackUrl, colorMode);
        }

        const url = new URL(callbackUrl, window.location.origin);

        if (source) {
            url.searchParams.append('source', source as string);
        }
        if (irf) {
            url.searchParams.append('irf', irf as string);
        }

        return setColorModeParam(url.toString(), colorMode);
    }

    private async handleAuth(data: HBAuthMessage) {
        const { userId, companyId, token, admin, auditeeCompanyId } = data;

        const callbackUrl = this.buildCallbackUrl(data);

        const isBookkeeper = isBookkeepingRoute(data);

        await signIn('hbTokenAuthentication', {
            userId,
            companyId,
            token,
            fingerprint: await getFingerprint(),
            admin: admin ? 'admin' : '',
            redirect: !!callbackUrl,
            callbackUrl,
            auditeeCompanyId: isBookkeeper ? auditeeCompanyId : ''
        });
    }

    async getSidenavState() {
        await this.ready;
        return this.adapter?.getSidenavState();
    }

    addCallback(name: CallbackNames, callback: (data: unknown) => void) {
        this.callbacks.set(name, callback);
    }

    removeCallback(name: CallbackNames) {
        this.callbacks.delete(name);
    }

    async getToken() {
        if (!this.initialized) {
            this.init();
        }
        await this.ready;
        return this.adapter?.getToken();
    }

    async toggleModal(isOpen: boolean) {
        await this.ready;
        return this.adapter?.toggleModal(isOpen);
    }

    async triggerAppCallback<Payload = void>(name: string) {
        await this.ready;
        return (await this.adapter?.triggerAppCallback(name)) as Promise<Payload>;
    }

    async signout() {
        await this.ready;
        return this.adapter?.signout();
    }
}
